/**
 * palletCodes — which pallet on a checklist a scanned or typed code names (2026-10-26).
 *
 * Mirror of jobq-api pallet_handoffs.resolve_code: keep the two in step (the API checks every
 * confirmation again). Scanning is an option, never required: a line is confirmed by a scan, by
 * typing the beacon number (BCN-020, 20) or the pallet number (PLT-42C7, 42C7), or by ticking it.
 *
 * Accepted:
 *   a scanned link   https://jobqworks.com/s/plt-493 (a beacon's screen QR) or /s/bcn-20
 *   a pallet number  PLT-42C7, plt 42c7, 42C7 (the 4x6 labels' Code 128 reads PLT-42C7)
 *   a beacon number  BCN-020, bcn20, 020, 20
 * A bare 4-character code is a pallet number first (a short id can be all digits), then a beacon
 * number. Only the listed pallets count.
 */

export const CODE_MAX = 200;

const LINK_RE = /(?:^|\/s\/)\s*(plt|bcn)[-\s]?(\d{1,9})\s*\/?\s*$/i;
const BCN_RE = /^BCN[-\s]?0*(\d{1,6})$/i;
const PLT_RE = /^PLT[-\s]?([0-9A-Z]{4})$/i;
const SHORT_RE = /^[0-9A-Z]{4}$/i;
const DIGITS_RE = /^0*(\d{1,6})$/;

/** BCN-020 -> 20; null for anything else. */
export function beaconNumber(beaconId) {
  const m = BCN_RE.exec(String(beaconId == null ? '' : beaconId).trim());
  return m ? parseInt(m[1], 10) : null;
}

function shortKey(shortId) {
  const m = PLT_RE.exec(String(shortId == null ? '' : shortId).trim());
  return m ? m[1].toUpperCase() : null;
}

function one(ids) {
  const set = Array.from(new Set(ids));
  return set.length === 1 ? set[0] : null;
}

/** {palletId, by: 'pallet' | 'beacon'} or null (not one of these pallets, unreadable, ambiguous). */
export function resolveCode(text, rows) {
  const raw = String(text == null ? '' : text).trim();
  if (!raw || raw.length > CODE_MAX) return null;
  const list = (rows || []).filter((r) => r && r.pallet_id != null);
  const byBeacon = (n) => one(list.filter((r) => beaconNumber(r.beacon_id) === n).map((r) => Number(r.pallet_id)));
  const byShort = (k) => one(list.filter((r) => shortKey(r.short_id) === k).map((r) => Number(r.pallet_id)));
  const hit = (palletId, by) => (palletId == null ? null : { palletId, by });

  let m = LINK_RE.exec(raw);
  if (m) {
    const key = parseInt(m[2], 10);
    if (m[1].toLowerCase() === 'plt') return hit(one(list.filter((r) => Number(r.pallet_id) === key).map((r) => Number(r.pallet_id))), 'pallet');
    return hit(byBeacon(key), 'beacon');
  }
  const compact = raw.replace(/\s+/g, '').toUpperCase();
  m = PLT_RE.exec(compact);
  if (m) return hit(byShort(m[1].toUpperCase()), 'pallet');
  m = BCN_RE.exec(compact);
  if (m) return hit(byBeacon(parseInt(m[1], 10)), 'beacon');
  if (SHORT_RE.test(compact)) {
    const p = byShort(compact);
    if (p != null) return { palletId: p, by: 'pallet' };
  }
  m = DIGITS_RE.exec(compact);
  if (m) {
    const p = byBeacon(parseInt(m[1], 10));
    if (p != null) return { palletId: p, by: 'beacon' };
  }
  return null;
}

// A keyboard-wedge scanner types each character <= 60 ms after the last (utils/scanWedge.js);
// a person types 100+ ms apart. The checklist's box only needs to say which it was for the record.
export const SCAN_GAP_MS = 60;
export const SCAN_MIN_CHARS = 4;

/** Tracks one entry's keystrokes: key(ev) for every character, then wasScan() at Enter. */
export function createEntryTimer() {
  let count = 0;
  let last = null;
  let slow = false;
  return {
    key(ev) {
      if (!ev || !ev.key || ev.key.length !== 1) return;
      const t = ev.timeStamp;
      if (last != null && t - last > SCAN_GAP_MS) slow = true;
      last = t;
      count += 1;
    },
    wasScan() { return count >= SCAN_MIN_CHARS && !slow; },
    reset() { count = 0; last = null; slow = false; },
  };
}
